import {
  ReportTooltipTemplateContext,
  ReportsTranslations,
  DatasetBase,
  ReportTooltipNormalizedParams,
} from '@core/reports-types';
import { SupportedNumberFormat, valueFormatter } from '@core/utils-legacy';

import { getPercentWithPrecision } from '../echarts-number-utils';
import { isSalaryMetric } from '../is-salary-metric';

const getParamValue = <T = string | number>(param: ReportTooltipNormalizedParams[0]) => {
  const value = Array.isArray(param.value) ? param.value : [param.value];
  return <T>value[param.encode!.x[0]];
};

const getPieParamValue = <T = string | number>(param: ReportTooltipNormalizedParams[0]) => {
  const value = Array.isArray(param.value) ? param.value : [param.value];
  return <T>value[1];
};

const getTotals = (dataset: DatasetBase) => dataset.calcTotals().map(([, total]) => total);

const getFooterText = (
  dataset: DatasetBase,
  translations: ReportsTranslations,
  params: ReportTooltipNormalizedParams,
) => {
  if (dataset.dataset.source.length === 1 || params.length === 1) {
    return {
      footerPrimaryText: undefined,
      footerSecondaryText: undefined,
    };
  }

  if (dataset.isSingleDimension) {
    return {
      footerPrimaryText: translations.i18n.translate('tlr.pct_of_total', {
        pct: getPercentWithPrecision(getTotals(dataset), params[0].dataIndex, 2),
      }),
      footerSecondaryText: undefined,
    };
  }

  const count = params.reduce((acc: number, param) => acc + getParamValue<number>(param), 0);
  const formatted = valueFormatter(
    count,
    {
      unitType: dataset.metric.unitType,
      unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
    },
    translations.i18n,
    {
      number: { format: SupportedNumberFormat.IntlStandard },
    },
  );

  const unitLabel = isSalaryMetric(dataset.metric)
    ? dataset.getCategoryNameById(params[0].name)
    : translations.i18n.translate(`tlr.resource_type_unit.${dataset.metric.resourceId}`, { count });

  return {
    footerPrimaryText: translations.i18n.translate('tlr.tooltips.total'),
    footerSecondaryText: unitLabel ? `${formatted} ${unitLabel}` : formatted,
  };
};

const getHeaderText = (
  dataset: DatasetBase,
  translations: ReportsTranslations,
  params: ReportTooltipNormalizedParams,
) =>
  !dataset.isSingleDimension
    ? dataset.getCategoryNameById(params[0].name)
    : dataset.metric.dataLabel;
export const breakdownTooltipContextFactory = (
  dataset: DatasetBase,
  translations: ReportsTranslations,
  p: ReportTooltipNormalizedParams,
  isPie: boolean,
): ReportTooltipTemplateContext => {
  const params = Array.isArray(p) ? p : [p];
  const nonEmpty = isPie ? params : params.filter(param => <number>getParamValue(param) > 0);
  const { footerPrimaryText, footerSecondaryText } = getFooterText(dataset, translations, nonEmpty);
  const headerText = getHeaderText(dataset, translations, params);

  const items = nonEmpty.reduce(
    (acc, param) => {
      acc.push({
        label: dataset.isSingleDimension
          ? dataset.getCategoryNameById(params[0].name)
          : dataset.getDimensionNameById(<string>param.seriesId),
        value: valueFormatter(
          isPie ? getPieParamValue(param) : getParamValue(param),
          dataset.metric,
          translations.i18n,
          {
            number: { format: SupportedNumberFormat.IntlStandard },
          },
        ),
        color: <string>param.color,
        id: param?.seriesId,
      });

      return acc;
    },
    <ReportTooltipTemplateContext['items']>[],
  );

  return {
    footerPrimaryText,
    footerSecondaryText,
    headerText,
    items,
  };
};
